import { X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useGetPurchaseOrderPDFQuery,
  useGetPurchaseOrderQuery,
  useAcceptPurchaseOrderMutation,
  useDeclinePurchaseOrderMutation,
} from "@/redux/services/purchaseOrder/purchaseOrderApi";
import { toast } from "sonner";

interface PurchaseOrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrderId: string | null;
  onStatusChange?: () => void;
}

export default function PurchaseOrderDetailsModal({
  isOpen,
  onClose,
  purchaseOrderId,
  onStatusChange,
}: PurchaseOrderDetailsModalProps) {
  // Fetch the single order to obtain document_path (primary method)
  const {
    data: singleOrderData,
    isLoading: isLoadingOrder,
    error: orderError,
    refetch: refetchOrder,
  } = useGetPurchaseOrderQuery(purchaseOrderId!, {
    skip: !purchaseOrderId || !isOpen,
  });

  // Fetch PDF blob as fallback (if needed)
  const {
    data: pdfData,
    isLoading: isLoadingPdf,
    error: pdfError,
  } = useGetPurchaseOrderPDFQuery(purchaseOrderId!, {
    skip: !purchaseOrderId || !isOpen,
  });

  // Accept and Decline mutations
  const [acceptOrder, { isLoading: isAccepting }] =
    useAcceptPurchaseOrderMutation();
  const [declineOrder, { isLoading: isDeclining }] =
    useDeclinePurchaseOrderMutation();

  const isLoading = isLoadingOrder || isLoadingPdf;
  const error = orderError || pdfError;

  // Get current status from the order data
  const currentStatus = singleOrderData?.data?.accepted_by_vendor;
  const isPending = currentStatus === null || currentStatus === undefined;

  const getDirectPdfUrl = (): string | null => {
    const documentPath = singleOrderData?.data?.document_path;
    if (!documentPath) return null;

    // Concatenate AWS URL with document path
    const awsUrl = import.meta.env.VITE_AWS_URL || "";
    if (awsUrl) {
      // Remove leading slash from document path if present
      const imagePath = documentPath.startsWith("/")
        ? documentPath.slice(1)
        : documentPath;
      // Ensure AWS URL ends with a slash
      const baseUrl = awsUrl.endsWith("/") ? awsUrl : `${awsUrl}/`;
      return `${baseUrl}${imagePath}`;
    }

    // Fallback to backend URL if AWS URL is not configured
    const apiBase =
      import.meta.env.VITE_PUBLIC_BACKEND_URL || "http://localhost:8000/api";
    const fileBase = apiBase.replace(/\/api\/?$/, "");
    return `${fileBase}${documentPath}`;
  };

  // Get the PDF URL - prioritize document_path from API response
  const pdfUrl = getDirectPdfUrl();

  // Handle accept order
  const handleAcceptOrder = async () => {
    if (!purchaseOrderId) return;

    try {
      const result = await acceptOrder(purchaseOrderId).unwrap();
      if (result?.success !== false) {
        toast.success(
          result?.message || "Purchase order accepted successfully"
        );
        refetchOrder();
        onStatusChange?.();
      } else {
        toast.error(result?.message || "Failed to accept purchase order");
      }
    } catch (error: any) {
      console.error("Accept order error:", error);
      toast.error(error?.data?.message || "Failed to accept purchase order");
    }
  };

  // Handle decline order
  const handleDeclineOrder = async () => {
    if (!purchaseOrderId) return;

    try {
      const result = await declineOrder(purchaseOrderId).unwrap();
      if (result?.success !== false) {
        toast.success(
          result?.message || "Purchase order declined successfully"
        );
        refetchOrder();
        onStatusChange?.();
      } else {
        toast.error(result?.message || "Failed to decline purchase order");
      }
    } catch (error: any) {
      console.error("Decline order error:", error);
      toast.error(error?.data?.message || "Failed to decline purchase order");
    }
  };

  // Handle download PDF
  const handleDownloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `PO-${singleOrderData?.data?.document_number || purchaseOrderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (pdfData) {
      const blob = new Blob([pdfData], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `PO-${singleOrderData?.data?.document_number || purchaseOrderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      toast.error("PDF not available for download");
    }
  };

  if (!isOpen || !purchaseOrderId) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Right Drawer */}
      <div className="absolute right-0 top-0 h-full w-[800px] bg-white shadow-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            Purchase Order PDF
          </h2>
          <div className="flex items-center gap-2">
            {/* Download PDF Button */}
            {(pdfUrl || pdfData) && !isLoading && (
              <Button
                onClick={handleDownloadPDF}
                variant="outline"
                size="sm"
                className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            )}
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* PDF Content */}
        <div className="flex-1 overflow-hidden">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading PDF...</p>
              </div>
            </div>
          )}

          {error && !isLoading && !pdfUrl && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-red-600 mb-2">Failed to load PDF</p>
                <p className="text-sm text-gray-500 mb-4">
                  {orderError
                    ? "Could not fetch purchase order details"
                    : "Could not load PDF document"}
                </p>
                <Button onClick={onClose} variant="outline">
                  Close
                </Button>
              </div>
            </div>
          )}

          {/* Prioritize document_path URL from API */}
          {pdfUrl && !isLoading && (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title="Purchase Order PDF"
              onError={(e) => {
                console.error("PDF iframe error:", e);
              }}
            />
          )}

          {/* Fallback to blob if document_path is not available */}
          {!pdfUrl && pdfData && !isLoading && (
            <iframe
              src={URL.createObjectURL(
                new Blob([pdfData], { type: "application/pdf" })
              )}
              className="w-full h-full border-0"
              title="Purchase Order PDF"
              onError={(e) => {
                console.error("PDF blob error:", e);
              }}
            />
          )}

          {/* Show error if neither method works */}
          {!pdfUrl && !pdfData && !isLoading && error && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-red-600 mb-2">Failed to load PDF</p>
                <p className="text-sm text-gray-500 mb-4">
                  The PDF document could not be loaded. Please try again later.
                </p>
                <Button onClick={onClose} variant="outline">
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons - Only show when status is pending, positioned at bottom right */}
        {isPending && !isLoading && (
          <div className="flex items-center justify-end gap-4 p-4 border-t bg-white">
            <Button
              onClick={handleDeclineOrder}
              disabled={isAccepting || isDeclining}
              className="bg-[#EF4444] hover:bg-[#DC2626] text-green font-semibold rounded-md px-6 py-2 border-0 shadow-none"
            >
              {isDeclining ? "Declining..." : "Reject Order"}
            </Button>
            <Button
              onClick={handleAcceptOrder}
              disabled={isAccepting || isDeclining}
              className="bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold rounded-md px-6 py-2 border-0 shadow-none"
            >
              {isAccepting ? "Accepting..." : "Accept Order"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
